<template>
  <v-dialog
    :value="value"
    style="z-index: 120000"
    :z-index="120000"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="watch-heart-rate-fullscreen-dialog"
    @input="close">
    <section class="watch-pulse-screen" role="dialog" aria-modal="true" aria-label="Pulsverlauf im Vollbild">
      <div class="watch-pulse-ambient watch-pulse-ambient--one"></div>
      <div class="watch-pulse-ambient watch-pulse-ambient--two"></div>

      <header class="watch-pulse-header">
        <button type="button" class="watch-pulse-close" aria-label="Pulsansicht schließen" @click="close(false)">
          <v-icon color="white">mdi-close</v-icon>
        </button>

        <div class="watch-pulse-heading">
          <span class="watch-pulse-eyebrow"><v-icon x-small color="#ff6b82">mdi-heart-pulse</v-icon> {{ sourceName.toUpperCase() }}</span>
          <h2>{{ title || "Pulsverlauf" }}</h2>
          <p v-if="subtitle">{{ subtitle }}</p>
        </div>

        <div class="watch-pulse-source">
          <span class="watch-pulse-source-dot"></span>
          {{ healthSourceName }}
        </div>
      </header>

      <main class="watch-pulse-content">
        <div class="watch-pulse-overview" aria-label="Pulsübersicht">
          <div class="watch-pulse-overview-card">
            <span>Durchschnitt</span>
            <strong>{{ displayMetric(average, fallbackAverage) }} <small>BPM</small></strong>
          </div>
          <div class="watch-pulse-overview-card">
            <span>Minimum</span>
            <strong>{{ displayMetric(minimum, fallbackMinimum) }} <small>BPM</small></strong>
          </div>
          <div class="watch-pulse-overview-card watch-pulse-overview-card--hot">
            <span>Maximum</span>
            <strong>{{ displayMetric(maximum, fallbackMaximum) }} <small>BPM</small></strong>
          </div>
          <div class="watch-pulse-overview-card watch-pulse-overview-card--desktop">
            <span>Messpunkte</span>
            <strong>{{ normalizedSamples.length }}</strong>
          </div>
        </div>

        <section class="watch-pulse-chart-card">
          <div class="watch-pulse-chart-toolbar">
            <div class="watch-pulse-instruction" :class="{ 'watch-pulse-instruction--active': selectedSample }">
              <template v-if="selectedSample">
                <span class="watch-pulse-instruction-dot"></span>
                {{ activeInterval ? activeInterval.title : "Training" }} · {{ formatElapsed(selectedSample.x) }}
              </template>
              <template v-else>
                <v-icon small color="#ff7890">mdi-gesture-tap-hold</v-icon>
                Finger halten und über die Kurve bewegen
              </template>
            </div>

            <div class="watch-pulse-controls" aria-label="Diagramm-Zoom">
              <button type="button" :disabled="!canZoomOut" aria-label="Herauszoomen" @click="zoomBy(1.55)">
                <v-icon small color="white">mdi-minus</v-icon>
              </button>
              <button type="button" class="watch-pulse-fit" :disabled="isFit" @click="resetViewport">
                {{ isFit ? "Gesamt" : zoomLabel }}
              </button>
              <button type="button" :disabled="!canZoomIn" aria-label="Hineinzoomen" @click="zoomBy(0.62)">
                <v-icon small color="white">mdi-plus</v-icon>
              </button>
            </div>
          </div>

          <div
            ref="stage"
            class="watch-pulse-stage"
            :class="{
              'watch-pulse-stage--scrubbing': gestureMode === 'scrub',
              'watch-pulse-stage--panning': gestureMode === 'pan' || gestureMode === 'pinch',
            }"
            tabindex="0"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerCancel"
            @wheel.prevent="onWheel"
            @keydown="onKeyDown">
            <svg
              v-if="normalizedSamples.length > 1"
              class="watch-pulse-svg"
              :viewBox="`0 0 ${stageWidth} ${stageHeight}`"
              role="img"
              aria-label="Interaktiver Pulsverlauf">
              <defs>
                <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#ff526d" stop-opacity="0.5" />
                  <stop offset="72%" stop-color="#ff526d" stop-opacity="0.09" />
                  <stop offset="100%" stop-color="#ff526d" stop-opacity="0" />
                </linearGradient>
                <filter :id="glowId" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <clipPath :id="clipId">
                  <rect
                    :x="geometry.left"
                    :y="geometry.top"
                    :width="geometry.width"
                    :height="geometry.height"
                    rx="10" />
                </clipPath>
              </defs>

              <g class="watch-pulse-grid">
                <g v-for="line in yGrid" :key="`y-${line.value}`">
                  <line
                    :x1="geometry.left"
                    :x2="geometry.right"
                    :y1="line.y"
                    :y2="line.y"
                    vector-effect="non-scaling-stroke" />
                  <text :x="geometry.left - 10" :y="line.y + 4" text-anchor="end">{{ line.value }}</text>
                </g>
              </g>

              <g :clip-path="`url(#${clipId})`">
                <rect
                  v-for="interval in visibleIntervals"
                  :key="interval.key"
                  :x="interval.screenStart"
                  :y="geometry.top"
                  :width="Math.max(2, interval.screenEnd - interval.screenStart)"
                  :height="geometry.height"
                  :fill="interval.color"
                  opacity="0.09" />

                <path v-if="areaPath" :d="areaPath" :fill="`url(#${gradientId})`" class="watch-pulse-area" />
                <path
                  v-if="linePath"
                  :d="linePath"
                  fill="none"
                  stroke="#ff6079"
                  stroke-width="3.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  vector-effect="non-scaling-stroke"
                  :filter="`url(#${glowId})`"
                  class="watch-pulse-line" />

                <g v-for="marker in visibleMarkers" :key="marker.key" class="watch-pulse-set-marker">
                  <line
                    :x1="marker.screenX"
                    :x2="marker.screenX"
                    :y1="geometry.top + 8"
                    :y2="geometry.bottom"
                    vector-effect="non-scaling-stroke" />
                  <circle :cx="marker.screenX" :cy="marker.screenY" r="5.5" vector-effect="non-scaling-stroke" />
                </g>

                <g v-if="selectedPoint" class="watch-pulse-cursor">
                  <line
                    :x1="selectedPoint.x"
                    :x2="selectedPoint.x"
                    :y1="geometry.top"
                    :y2="geometry.bottom"
                    vector-effect="non-scaling-stroke" />
                  <circle :cx="selectedPoint.x" :cy="selectedPoint.y" r="10" class="watch-pulse-cursor-halo" />
                  <circle :cx="selectedPoint.x" :cy="selectedPoint.y" r="5" class="watch-pulse-cursor-dot" />
                </g>
              </g>

              <g class="watch-pulse-time-axis">
                <g v-for="tick in timeTicks" :key="`x-${tick.value}`">
                  <line
                    :x1="tick.x"
                    :x2="tick.x"
                    :y1="geometry.bottom"
                    :y2="geometry.bottom + 6"
                    vector-effect="non-scaling-stroke" />
                  <text :x="tick.x" :y="geometry.bottom + 25" :text-anchor="tick.anchor">{{ formatElapsed(tick.value) }}</text>
                </g>
              </g>
            </svg>

            <div v-else class="watch-pulse-empty">
              <v-icon size="42" color="#5f6977">mdi-heart-off-outline</v-icon>
              <strong>Kein detaillierter Pulsverlauf</strong>
              <span>Für dieses Training wurden nur zusammengefasste Health-Daten gespeichert.</span>
            </div>

            <transition name="watch-pulse-bubble">
              <div v-if="selectedSample" class="watch-pulse-value-bubble" :style="selectedBubbleStyle">
                <div class="watch-pulse-value-main">
                  <v-icon small color="#ff6079">mdi-heart</v-icon>
                  <strong>{{ Math.round(selectedSample.y) }}</strong>
                  <span>BPM</span>
                </div>
                <div class="watch-pulse-value-meta">
                  <span>{{ formatElapsed(selectedSample.x) }}</span>
                  <span v-if="activeInterval">{{ activeInterval.title }}</span>
                </div>
              </div>
            </transition>

            <div
              class="watch-pulse-edge watch-pulse-edge--left"
              :class="{ 'watch-pulse-edge--active': autoScrollDirection < 0 }">
              <v-icon color="#ff7188">mdi-chevron-left</v-icon>
            </div>
            <div
              class="watch-pulse-edge watch-pulse-edge--right"
              :class="{ 'watch-pulse-edge--active': autoScrollDirection > 0 }">
              <v-icon color="#ff7188">mdi-chevron-right</v-icon>
            </div>
          </div>

          <footer class="watch-pulse-chart-footer">
            <div>
              <v-icon x-small color="#7f8a98">mdi-arrow-left-right</v-icon>
              Wischen zum Verschieben
            </div>
            <div>
              <v-icon x-small color="#7f8a98">mdi-gesture-pinch</v-icon>
              Zwei Finger zum Zoomen
            </div>
            <div v-if="autoScrollDirection" class="watch-pulse-autoscroll-label">
              Automatisch {{ autoScrollDirection > 0 ? "vorwärts" : "zurück" }}
            </div>
          </footer>
        </section>
      </main>
    </section>
  </v-dialog>
</template>

<script>
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value))

export default {
  name: "WatchHeartRateFullscreen",
  props: {
    value: { type: Boolean, default: false },
    samples: { type: Array, default: () => [] },
    intervals: { type: Array, default: () => [] },
    markers: { type: Array, default: () => [] },
    title: { type: String, default: "Pulsverlauf" },
    subtitle: { type: String, default: "" },
    sourceName: { type: String, default: "Watch" },
    healthSourceName: { type: String, default: "Gesundheitsdaten" },
    durationSeconds: { type: Number, default: 0 },
    average: { type: [Number, String], default: null },
    minimum: { type: [Number, String], default: null },
    maximum: { type: [Number, String], default: null },
  },
  data() {
    return {
      viewStart: 0,
      viewEnd: 1,
      selectedIndex: null,
      stageWidth: 1000,
      stageHeight: 520,
      gesture: null,
      gestureMode: null,
      pointerMap: null,
      holdTimer: null,
      autoScrollFrame: null,
      autoScrollDirection: 0,
      autoScrollIntensity: 0,
      autoScrollLastTime: null,
      inertiaFrame: null,
      resizeObserver: null,
    }
  },
  computed: {
    gradientId() {
      return `watch-pulse-gradient-${this._uid}`
    },
    glowId() {
      return `watch-pulse-glow-${this._uid}`
    },
    clipId() {
      return `watch-pulse-clip-${this._uid}`
    },
    normalizedSamples() {
      return (Array.isArray(this.samples) ? this.samples : [])
        .map((sample) => ({ x: Number(sample && sample.x), y: Number(sample && sample.y) }))
        .filter((sample) => Number.isFinite(sample.x) && sample.x >= 0 && Number.isFinite(sample.y) && sample.y > 0)
        .sort((left, right) => left.x - right.x)
    },
    normalizedIntervals() {
      const palette = ["#00e396", "#36a3ff", "#9b6cff", "#ff9f43", "#ff6079", "#4dd0e1"]
      return (Array.isArray(this.intervals) ? this.intervals : [])
        .map((interval, index) => ({
          key: interval.key || `interval-${index}`,
          title: String(interval.title || "Übung"),
          startOffset: Number(interval.startOffset),
          endOffset: Number(interval.endOffset),
          color: interval.color || palette[index % palette.length],
        }))
        .filter(
          (interval) =>
            Number.isFinite(interval.startOffset) &&
            Number.isFinite(interval.endOffset) &&
            interval.endOffset > interval.startOffset,
        )
    },
    normalizedMarkers() {
      return (Array.isArray(this.markers) ? this.markers : [])
        .map((marker, index) => ({
          ...marker,
          key: marker.key || `marker-${index}`,
          x: Number(marker.x),
          y: Number(marker.y),
        }))
        .filter((marker) => Number.isFinite(marker.x) && marker.x >= 0)
    },
    fullStart() {
      return 0
    },
    fullEnd() {
      const sampleEnd = this.normalizedSamples.length ? this.normalizedSamples[this.normalizedSamples.length - 1].x : 0
      const intervalEnd = this.normalizedIntervals.length ? Math.max(...this.normalizedIntervals.map((interval) => interval.endOffset)) : 0
      const markerEnd = this.normalizedMarkers.length ? Math.max(...this.normalizedMarkers.map((marker) => marker.x)) : 0
      return Math.max(1, Number(this.durationSeconds) || 0, sampleEnd, intervalEnd, markerEnd)
    },
    fullSpan() {
      return Math.max(1, this.fullEnd - this.fullStart)
    },
    viewSpan() {
      return Math.max(0.001, this.viewEnd - this.viewStart)
    },
    minimumViewSpan() {
      return Math.min(this.fullSpan, Math.max(12, Math.min(60, this.fullSpan / 24)))
    },
    isFit() {
      return this.viewSpan >= this.fullSpan - 0.5
    },
    canZoomIn() {
      return this.viewSpan > this.minimumViewSpan + 0.5
    },
    canZoomOut() {
      return !this.isFit
    },
    zoomLabel() {
      const factor = Math.max(1, this.fullSpan / this.viewSpan)
      return `${factor.toFixed(factor >= 10 ? 0 : 1)}×`
    },
    geometry() {
      const left = this.stageWidth < 560 ? 42 : 62
      const rightPadding = this.stageWidth < 560 ? 16 : 28
      const top = 26
      const bottomPadding = 48
      return {
        left,
        right: this.stageWidth - rightPadding,
        top,
        bottom: this.stageHeight - bottomPadding,
        width: Math.max(1, this.stageWidth - left - rightPadding),
        height: Math.max(1, this.stageHeight - top - bottomPadding),
      }
    },
    globalYRange() {
      if (!this.normalizedSamples.length) return { minimum: 40, maximum: 200 }
      const values = this.normalizedSamples.map((sample) => sample.y)
      const rawMinimum = Math.min(...values)
      const rawMaximum = Math.max(...values)
      const padding = Math.max(8, (rawMaximum - rawMinimum) * 0.14)
      return {
        minimum: Math.max(0, Math.floor(rawMinimum - padding)),
        maximum: Math.ceil(rawMaximum + padding),
      }
    },
    fallbackAverage() {
      if (!this.normalizedSamples.length) return null
      return this.normalizedSamples.reduce((sum, sample) => sum + sample.y, 0) / this.normalizedSamples.length
    },
    fallbackMinimum() {
      return this.normalizedSamples.length ? Math.min(...this.normalizedSamples.map((sample) => sample.y)) : null
    },
    fallbackMaximum() {
      return this.normalizedSamples.length ? Math.max(...this.normalizedSamples.map((sample) => sample.y)) : null
    },
    visibleSamples() {
      if (!this.normalizedSamples.length) return []
      let first = this.normalizedSamples.findIndex((sample) => sample.x >= this.viewStart)
      if (first < 0) first = this.normalizedSamples.length - 1
      first = Math.max(0, first - 1)
      let last = first
      while (last < this.normalizedSamples.length && this.normalizedSamples[last].x <= this.viewEnd) last += 1
      return this.normalizedSamples.slice(first, Math.min(this.normalizedSamples.length, last + 1))
    },
    mappedVisibleSamples() {
      return this.visibleSamples.map((sample) => ({ ...sample, ...this.mapSample(sample) }))
    },
    linePath() {
      const points = this.mappedVisibleSamples
      if (points.length < 2) return ""
      let path = `M ${points[0].screenX.toFixed(2)} ${points[0].screenY.toFixed(2)}`
      for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1]
        const current = points[index]
        const controlDistance = (current.screenX - previous.screenX) * 0.38
        path += ` C ${(previous.screenX + controlDistance).toFixed(2)} ${previous.screenY.toFixed(2)}, ${(current.screenX - controlDistance).toFixed(2)} ${current.screenY.toFixed(2)}, ${current.screenX.toFixed(2)} ${current.screenY.toFixed(2)}`
      }
      return path
    },
    areaPath() {
      if (!this.linePath || !this.mappedVisibleSamples.length) return ""
      const first = this.mappedVisibleSamples[0]
      const last = this.mappedVisibleSamples[this.mappedVisibleSamples.length - 1]
      return `${this.linePath} L ${last.screenX.toFixed(2)} ${this.geometry.bottom.toFixed(2)} L ${first.screenX.toFixed(2)} ${this.geometry.bottom.toFixed(2)} Z`
    },
    yGrid() {
      const count = 4
      const range = this.globalYRange.maximum - this.globalYRange.minimum
      return Array.from({ length: count + 1 }, (_, index) => {
        const ratio = index / count
        const value = this.globalYRange.maximum - range * ratio
        return {
          value: Math.round(value),
          y: this.geometry.top + this.geometry.height * ratio,
        }
      })
    },
    timeTicks() {
      const count = this.stageWidth < 560 ? 3 : 5
      return Array.from({ length: count + 1 }, (_, index) => {
        const ratio = index / count
        return {
          value: this.viewStart + this.viewSpan * ratio,
          x: this.geometry.left + this.geometry.width * ratio,
          anchor: index === 0 ? "start" : index === count ? "end" : "middle",
        }
      })
    },
    visibleIntervals() {
      return this.normalizedIntervals
        .filter((interval) => interval.endOffset >= this.viewStart && interval.startOffset <= this.viewEnd)
        .map((interval) => ({
          ...interval,
          screenStart: this.timeToScreenX(Math.max(this.viewStart, interval.startOffset)),
          screenEnd: this.timeToScreenX(Math.min(this.viewEnd, interval.endOffset)),
        }))
    },
    visibleMarkers() {
      return this.normalizedMarkers
        .filter((marker) => marker.x >= this.viewStart && marker.x <= this.viewEnd)
        .map((marker) => {
          const pulse = Number.isFinite(marker.y) && marker.y > 0 ? marker.y : this.nearestSample(marker.x)?.y
          return {
            ...marker,
            screenX: this.timeToScreenX(marker.x),
            screenY: this.valueToScreenY(pulse || this.globalYRange.minimum),
          }
        })
    },
    selectedSample() {
      if (this.selectedIndex === null) return null
      return this.normalizedSamples[this.selectedIndex] || null
    },
    selectedPoint() {
      return this.selectedSample ? this.mapSample(this.selectedSample) : null
    },
    selectedBubbleStyle() {
      if (!this.selectedPoint) return {}
      const percent = clamp((this.selectedPoint.x / this.stageWidth) * 100, 16, 84)
      return { left: `${percent}%` }
    },
    activeInterval() {
      if (!this.selectedSample) return null
      return (
        this.normalizedIntervals.find(
          (interval) => this.selectedSample.x >= interval.startOffset && this.selectedSample.x <= interval.endOffset,
        ) || null
      )
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.resetViewport()
          this.startMeasuringStage()
          if (this.$refs.stage) this.$refs.stage.focus({ preventScroll: true })
        })
      } else {
        this.stopInteractions()
        this.stopMeasuringStage()
      }
    },
    samples() {
      if (this.value) this.$nextTick(this.resetViewport)
    },
  },
  mounted() {
    this.pointerMap = new Map()
    if (this.value) {
      this.resetViewport()
      this.startMeasuringStage()
    }
  },
  beforeDestroy() {
    this.stopInteractions()
    this.stopMeasuringStage()
  },
  methods: {
    close(nextValue) {
      if (nextValue === false || nextValue === undefined) this.$emit("input", false)
    },
    displayMetric(primary, fallback) {
      const primaryNumber = Number(primary)
      if (primary !== null && primary !== undefined && Number.isFinite(primaryNumber) && primaryNumber > 0) {
        return Math.round(primaryNumber)
      }
      return Number.isFinite(fallback) ? Math.round(fallback) : "–"
    },
    formatElapsed(seconds) {
      const total = Math.max(0, Math.round(Number(seconds) || 0))
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const remainingSeconds = total % 60
      return hours > 0
        ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
        : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`
    },
    mapSample(sample) {
      return {
        x: this.timeToScreenX(sample.x),
        y: this.valueToScreenY(sample.y),
        screenX: this.timeToScreenX(sample.x),
        screenY: this.valueToScreenY(sample.y),
      }
    },
    timeToScreenX(time) {
      const ratio = (Number(time) - this.viewStart) / this.viewSpan
      return this.geometry.left + ratio * this.geometry.width
    },
    valueToScreenY(value) {
      const range = Math.max(1, this.globalYRange.maximum - this.globalYRange.minimum)
      const ratio = (Number(value) - this.globalYRange.minimum) / range
      return this.geometry.bottom - ratio * this.geometry.height
    },
    resetViewport() {
      this.viewStart = this.fullStart
      this.viewEnd = this.fullEnd
      this.selectedIndex = null
      this.stopInteractions()
      this.$nextTick(this.measureStage)
    },
    setViewport(start, span) {
      const safeSpan = clamp(Number(span) || this.fullSpan, this.minimumViewSpan, this.fullSpan)
      const maximumStart = Math.max(this.fullStart, this.fullEnd - safeSpan)
      const safeStart = clamp(Number(start) || 0, this.fullStart, maximumStart)
      this.viewStart = safeStart
      this.viewEnd = safeStart + safeSpan
    },
    zoomBy(factor, anchorTime = null) {
      this.cancelInertia()
      const currentSpan = this.viewSpan
      const newSpan = clamp(currentSpan * factor, this.minimumViewSpan, this.fullSpan)
      const anchor = Number.isFinite(anchorTime)
        ? anchorTime
        : this.selectedSample && this.selectedSample.x >= this.viewStart && this.selectedSample.x <= this.viewEnd
          ? this.selectedSample.x
          : this.viewStart + currentSpan / 2
      const ratio = clamp((anchor - this.viewStart) / currentSpan, 0, 1)
      this.setViewport(anchor - newSpan * ratio, newSpan)
    },
    panBy(seconds) {
      this.setViewport(this.viewStart + seconds, this.viewSpan)
    },
    startMeasuringStage() {
      this.stopMeasuringStage()
      this.$nextTick(() => {
        this.measureStage()
        if (typeof ResizeObserver !== "undefined" && this.$refs.stage) {
          this.resizeObserver = new ResizeObserver(this.measureStage)
          this.resizeObserver.observe(this.$refs.stage)
        } else {
          window.addEventListener("resize", this.measureStage)
        }
      })
    },
    stopMeasuringStage() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      window.removeEventListener("resize", this.measureStage)
    },
    measureStage() {
      const stage = this.$refs.stage
      if (!stage) return
      const rect = stage.getBoundingClientRect()
      this.stageWidth = Math.max(280, Math.round(rect.width))
      this.stageHeight = Math.max(300, Math.round(rect.height))
    },
    onPointerDown(event) {
      if (!this.normalizedSamples.length) return
      event.preventDefault()
      this.cancelInertia()
      if (!this.pointerMap) this.pointerMap = new Map()
      this.pointerMap.set(event.pointerId, { x: event.clientX, y: event.clientY })
      if (this.$refs.stage && this.$refs.stage.setPointerCapture) {
        try {
          this.$refs.stage.setPointerCapture(event.pointerId)
        } catch (error) {
          // Pointer capture is optional on older embedded WebViews.
        }
      }

      if (this.pointerMap.size >= 2) {
        this.beginPinch()
        return
      }

      const now = Date.now()
      this.gesture = {
        pointerId: event.pointerId,
        mode: "pending",
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        lastAt: now,
        velocityPx: 0,
        startViewStart: this.viewStart,
        startViewEnd: this.viewEnd,
      }
      this.gestureMode = "pending"
      this.clearHoldTimer()
      this.holdTimer = window.setTimeout(() => this.beginScrub(event.clientX), 170)
    },
    onPointerMove(event) {
      if (!this.pointerMap || !this.pointerMap.has(event.pointerId)) return
      event.preventDefault()
      this.pointerMap.set(event.pointerId, { x: event.clientX, y: event.clientY })

      if (this.pointerMap.size >= 2) {
        if (!this.gesture || this.gesture.mode !== "pinch") this.beginPinch()
        this.updatePinch()
        return
      }

      if (!this.gesture || this.gesture.pointerId !== event.pointerId) return
      const distanceX = event.clientX - this.gesture.startX
      const distanceY = event.clientY - this.gesture.startY

      if (this.gesture.mode === "pending" && Math.hypot(distanceX, distanceY) > 9) {
        this.clearHoldTimer()
        this.gesture.mode = "pan"
        this.gestureMode = "pan"
      }

      if (this.gesture.mode === "pan") {
        const rect = this.$refs.stage.getBoundingClientRect()
        const plotWidth = Math.max(1, rect.width)
        const desiredStart = this.gesture.startViewStart - (distanceX / plotWidth) * this.viewSpan
        this.setViewport(desiredStart, this.viewSpan)
        const now = Date.now()
        const elapsed = Math.max(1, now - this.gesture.lastAt)
        const instantaneousVelocity = (event.clientX - this.gesture.lastX) / elapsed
        this.gesture.velocityPx = this.gesture.velocityPx * 0.72 + instantaneousVelocity * 0.28
        this.gesture.lastAt = now
        this.gesture.lastX = event.clientX
        return
      }

      if (this.gesture.mode === "scrub") {
        this.gesture.lastX = event.clientX
        this.gesture.lastY = event.clientY
        this.updateSelectionAt(event.clientX)
        this.updateAutoScroll(event.clientX)
      }
    },
    onPointerUp(event) {
      if (!this.pointerMap || !this.pointerMap.has(event.pointerId)) return
      event.preventDefault()
      const mode = this.gesture && this.gesture.mode
      const velocity = this.gesture && this.gesture.velocityPx
      this.clearHoldTimer()
      this.stopAutoScroll()

      if (mode === "pending") this.updateSelectionAt(event.clientX)
      if (mode === "pan" && Number.isFinite(velocity)) this.startInertia(velocity)

      if (mode === "pinch") this.pointerMap.clear()
      else this.pointerMap.delete(event.pointerId)
      this.gesture = null
      this.gestureMode = null
    },
    onPointerCancel(event) {
      if (this.pointerMap) this.pointerMap.delete(event.pointerId)
      this.clearHoldTimer()
      this.stopAutoScroll()
      this.gesture = null
      this.gestureMode = null
    },
    beginScrub(clientX) {
      if (!this.gesture || this.gesture.mode !== "pending") return
      this.gesture.mode = "scrub"
      this.gestureMode = "scrub"
      this.updateSelectionAt(this.gesture.lastX || clientX)
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8)
    },
    beginPinch() {
      this.clearHoldTimer()
      this.stopAutoScroll()
      const points = Array.from(this.pointerMap.values()).slice(0, 2)
      if (points.length < 2) return
      const distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y))
      const midpointX = (points[0].x + points[1].x) / 2
      const ratio = this.pointerRatio(midpointX)
      this.gesture = {
        mode: "pinch",
        initialDistance: distance,
        initialSpan: this.viewSpan,
        anchorRatio: ratio,
        anchorTime: this.viewStart + this.viewSpan * ratio,
      }
      this.gestureMode = "pinch"
    },
    updatePinch() {
      if (!this.gesture || this.gesture.mode !== "pinch") return
      const points = Array.from(this.pointerMap.values()).slice(0, 2)
      if (points.length < 2) return
      const distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y))
      const newSpan = clamp(
        this.gesture.initialSpan * (this.gesture.initialDistance / distance),
        this.minimumViewSpan,
        this.fullSpan,
      )
      this.setViewport(this.gesture.anchorTime - newSpan * this.gesture.anchorRatio, newSpan)
    },
    pointerRatio(clientX) {
      const stage = this.$refs.stage
      if (!stage) return 0.5
      const rect = stage.getBoundingClientRect()
      const left = rect.left + (this.geometry.left / this.stageWidth) * rect.width
      const width = (this.geometry.width / this.stageWidth) * rect.width
      return clamp((clientX - left) / Math.max(1, width), 0, 1)
    },
    timeAtClientX(clientX) {
      return this.viewStart + this.pointerRatio(clientX) * this.viewSpan
    },
    updateSelectionAt(clientX) {
      const index = this.nearestSampleIndex(this.timeAtClientX(clientX))
      if (index !== null) this.selectedIndex = index
    },
    nearestSampleIndex(time) {
      const samples = this.normalizedSamples
      if (!samples.length) return null
      let low = 0
      let high = samples.length - 1
      while (low < high) {
        const middle = Math.floor((low + high) / 2)
        if (samples[middle].x < time) low = middle + 1
        else high = middle
      }
      if (low === 0) return 0
      const previous = samples[low - 1]
      const current = samples[low]
      return Math.abs(previous.x - time) <= Math.abs(current.x - time) ? low - 1 : low
    },
    nearestSample(time) {
      const index = this.nearestSampleIndex(time)
      return index === null ? null : this.normalizedSamples[index]
    },
    updateAutoScroll(clientX) {
      if (this.isFit || !this.$refs.stage) {
        this.stopAutoScroll()
        return
      }
      const rect = this.$refs.stage.getBoundingClientRect()
      const localX = clientX - rect.left
      const edgeWidth = Math.min(84, Math.max(48, rect.width * 0.16))
      let direction = 0
      let intensity = 0
      if (localX < edgeWidth && this.viewStart > this.fullStart) {
        direction = -1
        intensity = clamp((edgeWidth - localX) / edgeWidth, 0.18, 1)
      } else if (localX > rect.width - edgeWidth && this.viewEnd < this.fullEnd) {
        direction = 1
        intensity = clamp((localX - (rect.width - edgeWidth)) / edgeWidth, 0.18, 1)
      }

      this.autoScrollDirection = direction
      this.autoScrollIntensity = intensity
      if (direction && !this.autoScrollFrame) {
        this.autoScrollLastTime = null
        this.autoScrollFrame = window.requestAnimationFrame(this.autoScrollStep)
      } else if (!direction) {
        this.stopAutoScroll()
      }
    },
    autoScrollStep(timestamp) {
      this.autoScrollFrame = null
      if (!this.autoScrollDirection || !this.gesture || this.gesture.mode !== "scrub") return
      const elapsed = this.autoScrollLastTime === null ? 16 : Math.min(40, timestamp - this.autoScrollLastTime)
      this.autoScrollLastTime = timestamp
      const shift = this.autoScrollDirection * this.autoScrollIntensity * this.viewSpan * 0.34 * (elapsed / 1000)
      const before = this.viewStart
      this.panBy(shift)
      this.updateSelectionAt(this.gesture.lastX)
      if (Math.abs(this.viewStart - before) < 0.0001) {
        this.stopAutoScroll()
        return
      }
      this.autoScrollFrame = window.requestAnimationFrame(this.autoScrollStep)
    },
    stopAutoScroll() {
      if (this.autoScrollFrame) window.cancelAnimationFrame(this.autoScrollFrame)
      this.autoScrollFrame = null
      this.autoScrollDirection = 0
      this.autoScrollIntensity = 0
      this.autoScrollLastTime = null
    },
    startInertia(velocityPx) {
      if (Math.abs(velocityPx) < 0.05 || this.isFit || !this.$refs.stage) return
      this.cancelInertia()
      const rect = this.$refs.stage.getBoundingClientRect()
      let velocity = -(velocityPx / Math.max(1, rect.width)) * this.viewSpan
      let lastTime = null
      const step = (timestamp) => {
        const elapsed = lastTime === null ? 16 : Math.min(34, timestamp - lastTime)
        lastTime = timestamp
        const before = this.viewStart
        this.panBy(velocity * elapsed)
        velocity *= Math.pow(0.91, elapsed / 16)
        if (Math.abs(velocity) < 0.0004 || Math.abs(this.viewStart - before) < 0.0001) {
          this.inertiaFrame = null
          return
        }
        this.inertiaFrame = window.requestAnimationFrame(step)
      }
      this.inertiaFrame = window.requestAnimationFrame(step)
    },
    cancelInertia() {
      if (this.inertiaFrame) window.cancelAnimationFrame(this.inertiaFrame)
      this.inertiaFrame = null
    },
    clearHoldTimer() {
      if (this.holdTimer) window.clearTimeout(this.holdTimer)
      this.holdTimer = null
    },
    stopInteractions() {
      this.clearHoldTimer()
      this.stopAutoScroll()
      this.cancelInertia()
      if (this.pointerMap) this.pointerMap.clear()
      this.gesture = null
      this.gestureMode = null
    },
    onWheel(event) {
      const anchor = this.timeAtClientX(event.clientX)
      this.zoomBy(event.deltaY < 0 ? 0.82 : 1.22, anchor)
    },
    onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault()
        this.close(false)
      } else if (event.key === "ArrowLeft") {
        event.preventDefault()
        this.panBy(-this.viewSpan * 0.12)
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        this.panBy(this.viewSpan * 0.12)
      } else if (event.key === "+" || event.key === "=") {
        event.preventDefault()
        this.zoomBy(0.72)
      } else if (event.key === "-") {
        event.preventDefault()
        this.zoomBy(1.38)
      }
    },
  },
}
</script>

<style>
.watch-heart-rate-fullscreen-dialog.v-dialog {
  margin: 0 !important;
  overflow: hidden !important;
  max-height: none !important;
  background: #060808;
}

.watch-pulse-screen {
  --watch-pulse-border: rgba(255, 255, 255, 0.09);
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(circle at 12% 4%, rgba(255, 71, 104, 0.16), transparent 32%),
    radial-gradient(circle at 92% 90%, rgba(0, 227, 150, 0.1), transparent 35%),
    linear-gradient(145deg, #0a0d0d 0%, #050707 58%, #090708 100%);
  display: flex;
  flex-direction: column;
  overscroll-behavior: none;
}

.watch-pulse-ambient {
  position: absolute;
  width: 36vw;
  height: 36vw;
  min-width: 280px;
  min-height: 280px;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.12;
  pointer-events: none;
}

.watch-pulse-ambient--one {
  top: -18vw;
  left: 12vw;
  background: #ff3f61;
}

.watch-pulse-ambient--two {
  right: -16vw;
  bottom: -20vw;
  background: #00e396;
}

.watch-pulse-header {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;
  padding: max(18px, env(safe-area-inset-top)) max(22px, env(safe-area-inset-right)) 16px max(22px, env(safe-area-inset-left));
  border-bottom: 1px solid var(--watch-pulse-border);
  background: rgba(6, 9, 9, 0.76);
  -webkit-backdrop-filter: blur(22px) saturate(150%);
  backdrop-filter: blur(22px) saturate(150%);
}

.watch-pulse-close,
.watch-pulse-controls button {
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  background: rgba(255, 255, 255, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.watch-pulse-close {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  transition: transform 180ms ease, background 180ms ease;
}

.watch-pulse-close:active {
  transform: scale(0.92);
  background: rgba(255, 255, 255, 0.14);
}

.watch-pulse-heading {
  min-width: 0;
}

.watch-pulse-eyebrow {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #ff7890;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1.4px;
}

.watch-pulse-heading h2 {
  margin: 3px 0 0;
  overflow: hidden;
  color: #fff;
  font-size: clamp(18px, 2.2vw, 27px);
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.watch-pulse-heading p {
  margin: 3px 0 0;
  overflow: hidden;
  color: #87919d;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.watch-pulse-source {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(0, 227, 150, 0.24);
  border-radius: 999px;
  color: #9ff4cf;
  background: rgba(0, 227, 150, 0.08);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
}

.watch-pulse-source-dot,
.watch-pulse-instruction-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #00e396;
  box-shadow: 0 0 12px rgba(0, 227, 150, 0.8);
}

.watch-pulse-content {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px max(18px, env(safe-area-inset-right)) max(14px, env(safe-area-inset-bottom)) max(18px, env(safe-area-inset-left));
}

.watch-pulse-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.watch-pulse-overview-card {
  position: relative;
  overflow: hidden;
  padding: 12px 14px;
  border: 1px solid var(--watch-pulse-border);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025));
}

.watch-pulse-overview-card::after {
  content: "";
  position: absolute;
  inset: auto 12px 0;
  height: 2px;
  border-radius: 2px;
  background: #ff6079;
  opacity: 0.52;
}

.watch-pulse-overview-card--hot::after {
  opacity: 1;
  box-shadow: 0 0 12px rgba(255, 82, 109, 0.72);
}

.watch-pulse-overview-card span {
  display: block;
  color: #7f8996;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.watch-pulse-overview-card strong {
  display: block;
  margin-top: 2px;
  color: #fff;
  font-size: clamp(20px, 3.5vw, 31px);
  line-height: 1.15;
}

.watch-pulse-overview-card small {
  color: #8a949f;
  font-size: 10px;
  font-weight: 800;
}

.watch-pulse-chart-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--watch-pulse-border);
  border-radius: 22px;
  background: linear-gradient(155deg, rgba(21, 25, 25, 0.94), rgba(8, 11, 11, 0.96));
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.42);
}

.watch-pulse-chart-toolbar,
.watch-pulse-chart-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 14px;
}

.watch-pulse-chart-toolbar {
  border-bottom: 1px solid var(--watch-pulse-border);
}

.watch-pulse-instruction {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  overflow: hidden;
  color: #9aa4af;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.watch-pulse-instruction--active {
  color: #dfe5e8;
}

.watch-pulse-controls {
  flex: none;
  display: grid;
  grid-template-columns: 34px minmax(56px, auto) 34px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}

.watch-pulse-controls button {
  height: 32px;
  border: 0;
  border-radius: 0;
  font-size: 11px;
  font-weight: 900;
}

.watch-pulse-controls button + button {
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.watch-pulse-controls button:disabled {
  cursor: default;
  opacity: 0.34;
}

.watch-pulse-controls .watch-pulse-fit {
  padding: 0 9px;
  color: #cdd4d9;
  letter-spacing: 0.3px;
}

.watch-pulse-stage {
  position: relative;
  flex: 1;
  min-height: 300px;
  overflow: hidden;
  outline: none;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.watch-pulse-stage--scrubbing {
  cursor: crosshair;
}

.watch-pulse-stage--panning {
  cursor: grabbing;
}

.watch-pulse-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.watch-pulse-grid line {
  stroke: rgba(255, 255, 255, 0.075);
  stroke-width: 1;
  stroke-dasharray: 4 6;
}

.watch-pulse-grid text,
.watch-pulse-time-axis text {
  fill: #66717d;
  font-size: 10px;
  font-weight: 700;
}

.watch-pulse-time-axis line {
  stroke: rgba(255, 255, 255, 0.13);
}

.watch-pulse-line {
  animation: watch-pulse-line-in 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.watch-pulse-area {
  animation: watch-pulse-area-in 600ms ease both;
}

.watch-pulse-set-marker line {
  stroke: rgba(0, 227, 150, 0.28);
  stroke-width: 1;
  stroke-dasharray: 3 5;
}

.watch-pulse-set-marker circle {
  fill: #00e396;
  stroke: #0d1512;
  stroke-width: 3;
}

.watch-pulse-cursor line {
  stroke: rgba(255, 255, 255, 0.62);
  stroke-width: 1;
  stroke-dasharray: 3 4;
}

.watch-pulse-cursor-halo {
  fill: rgba(255, 82, 109, 0.2);
  transform-box: fill-box;
  transform-origin: center;
  animation: watch-pulse-cursor-pulse 1.6s ease-in-out infinite;
}

.watch-pulse-cursor-dot {
  fill: #ff526d;
  stroke: #fff;
  stroke-width: 2;
}

.watch-pulse-value-bubble {
  position: absolute;
  z-index: 5;
  top: 18px;
  min-width: 134px;
  max-width: min(230px, 68vw);
  padding: 10px 12px;
  border: 1px solid rgba(255, 96, 121, 0.35);
  border-radius: 14px;
  color: #fff;
  background: rgba(18, 10, 12, 0.9);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.42), 0 0 24px rgba(255, 82, 109, 0.12);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  transform: translateX(-50%);
  pointer-events: none;
}

.watch-pulse-value-main {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.watch-pulse-value-main strong {
  color: #fff;
  font-size: 25px;
  line-height: 1;
}

.watch-pulse-value-main span {
  color: #ff8296;
  font-size: 10px;
  font-weight: 900;
}

.watch-pulse-value-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  color: #9aa3ad;
  font-size: 10px;
  font-weight: 700;
}

.watch-pulse-value-meta span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.watch-pulse-edge {
  position: absolute;
  z-index: 4;
  top: 50%;
  width: 52px;
  height: 38%;
  display: flex;
  align-items: center;
  opacity: 0;
  transform: translateY(-50%) scale(0.9);
  transition: opacity 160ms ease, transform 160ms ease;
  pointer-events: none;
}

.watch-pulse-edge--left {
  left: 0;
  justify-content: flex-start;
  padding-left: 7px;
  background: linear-gradient(90deg, rgba(255, 82, 109, 0.2), transparent);
}

.watch-pulse-edge--right {
  right: 0;
  justify-content: flex-end;
  padding-right: 7px;
  background: linear-gradient(-90deg, rgba(255, 82, 109, 0.2), transparent);
}

.watch-pulse-edge--active {
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.watch-pulse-chart-footer {
  min-height: 42px;
  justify-content: flex-start;
  border-top: 1px solid var(--watch-pulse-border);
  color: #76818c;
  font-size: 10px;
  font-weight: 700;
}

.watch-pulse-chart-footer > div {
  display: flex;
  align-items: center;
  gap: 5px;
}

.watch-pulse-autoscroll-label {
  margin-left: auto;
  color: #ff7b90;
  animation: watch-pulse-autoscroll 900ms ease-in-out infinite alternate;
}

.watch-pulse-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #747e89;
  text-align: center;
}

.watch-pulse-empty strong {
  color: #d5dadd;
}

.watch-pulse-empty span {
  max-width: 420px;
  font-size: 12px;
}

.watch-pulse-bubble-enter-active,
.watch-pulse-bubble-leave-active {
  transition: opacity 160ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.watch-pulse-bubble-enter,
.watch-pulse-bubble-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px) scale(0.94);
}

@keyframes watch-pulse-line-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes watch-pulse-area-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes watch-pulse-cursor-pulse {
  0%, 100% { transform: scale(0.82); opacity: 0.45; }
  50% { transform: scale(1.18); opacity: 1; }
}

@keyframes watch-pulse-autoscroll {
  from { opacity: 0.55; }
  to { opacity: 1; }
}

@media (max-width: 640px) {
  .watch-pulse-header {
    gap: 11px;
    padding-top: max(12px, env(safe-area-inset-top));
    padding-right: max(12px, env(safe-area-inset-right));
    padding-left: max(12px, env(safe-area-inset-left));
  }

  .watch-pulse-close {
    width: 40px;
    height: 40px;
  }

  .watch-pulse-source {
    width: 38px;
    height: 38px;
    justify-content: center;
    padding: 0;
    font-size: 0;
  }

  .watch-pulse-content {
    gap: 10px;
    padding-top: 10px;
    padding-right: max(9px, env(safe-area-inset-right));
    padding-left: max(9px, env(safe-area-inset-left));
  }

  .watch-pulse-overview {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
  }

  .watch-pulse-overview-card {
    padding: 9px 8px;
    border-radius: 13px;
  }

  .watch-pulse-overview-card--desktop {
    display: none;
  }

  .watch-pulse-overview-card span {
    font-size: 8px;
    letter-spacing: 0.35px;
  }

  .watch-pulse-overview-card strong {
    font-size: 19px;
  }

  .watch-pulse-chart-card {
    border-radius: 18px;
  }

  .watch-pulse-chart-toolbar {
    padding: 8px 9px;
  }

  .watch-pulse-instruction {
    font-size: 10px;
  }

  .watch-pulse-instruction .v-icon {
    display: none;
  }

  .watch-pulse-controls {
    grid-template-columns: 31px minmax(48px, auto) 31px;
  }

  .watch-pulse-controls button {
    height: 30px;
  }

  .watch-pulse-stage {
    min-height: 290px;
  }

  .watch-pulse-chart-footer {
    min-height: 38px;
    padding: 8px 10px;
  }

  .watch-pulse-chart-footer > div:nth-child(2) {
    display: none;
  }
}

@media (max-height: 650px) {
  .watch-pulse-heading p,
  .watch-pulse-overview-card span,
  .watch-pulse-chart-footer {
    display: none;
  }

  .watch-pulse-overview-card {
    padding-top: 6px;
    padding-bottom: 7px;
  }

  .watch-pulse-content {
    padding-top: 8px;
    gap: 8px;
  }

  .watch-pulse-stage {
    min-height: 240px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .watch-pulse-screen *,
  .watch-pulse-screen *::before,
  .watch-pulse-screen *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
  }
}
</style>
